import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  adminAlertsService,
  AlertAdmin,
  AlertRule,
  AlertRuleInput,
  AlertType,
} from '@/services/adminAlertsService';
import { CATEGORY_LABELS, CATEGORY_ORDER, ROLE_SINGULAR, roleLabel } from './alertLabels';
import { useHasChanges } from '@/hooks/useHasChanges';

type WordingField = 'subject' | 'email' | 'sms';

interface AlertRuleDialogProps {
  open: boolean;
  rule: AlertRule | null;
  initialAlertType?: string;
  types: AlertType[];
  roles: string[];
  admins: AlertAdmin[];
  onClose: () => void;
  onSaved: (rule: AlertRule) => void;
}

interface ChipListInputProps {
  label: string;
  placeholder: string;
  values: string[];
  onChange: (values: string[]) => void;
  helperText?: string;
}

const ChipListInput: React.FC<ChipListInputProps> = ({ label, placeholder, values, onChange, helperText }) => {
  const [input, setInput] = useState('');

  const add = (raw: string) => {
    const items = raw.split(',').map(v => v.trim()).filter(Boolean);
    if (!items.length) return;
    onChange(Array.from(new Set([...values, ...items])));
    setInput('');
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={values}
      inputValue={input}
      onInputChange={(_e, value, reason) => {
        if (reason === 'reset') return;
        if (value.includes(',')) add(value);
        else setInput(value);
      }}
      onChange={(_e, value) => {
        onChange(Array.from(new Set((value as string[]).map(v => v.trim()).filter(Boolean))));
        setInput('');
      }}
      onBlur={() => add(input)}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
        ))
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={values.length ? '' : placeholder}
          helperText={helperText}
        />
      )}
    />
  );
};

export const AlertRuleDialog: React.FC<AlertRuleDialogProps> = ({
  open,
  rule,
  initialAlertType,
  types,
  roles,
  admins,
  onClose,
  onSaved,
}) => {
  const [alertType, setAlertType] = useState('');
  const [name, setName] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [recipientRoles, setRecipientRoles] = useState<string[]>([]);
  const [recipientAdminIds, setRecipientAdminIds] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [extraPhones, setExtraPhones] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sms, setSms] = useState('');
  const [wordingOpen, setWordingOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<Record<string, unknown> | null>(null);

  const fieldRefs = {
    subject: useRef<HTMLInputElement | HTMLTextAreaElement | null>(null),
    email: useRef<HTMLInputElement | HTMLTextAreaElement | null>(null),
    sms: useRef<HTMLInputElement | HTMLTextAreaElement | null>(null),
  };
  const [focusedField, setFocusedField] = useState<WordingField>('email');

  const type = useMemo(() => types.find(t => t.key === alertType), [types, alertType]);

  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)),
    [types]
  );

  useEffect(() => {
    if (!open) return;
    const key = rule?.alertType || initialAlertType || '';
    const t = types.find(x => x.key === key);
    setAlertType(key);
    setName(rule?.name || t?.name || '');
    setSendEmail(rule ? rule.sendEmail : true);
    setSendSms(rule ? rule.sendSms : false);
    setRecipientRoles(rule ? rule.recipientRoles : ['super_admin', 'admin']);
    setRecipientAdminIds(rule?.recipientAdminIds || []);
    setExtraEmails(rule?.extraEmails || []);
    setExtraPhones(rule?.extraPhones || []);
    setSubject(rule?.subjectTemplate ?? t?.defaultSubject ?? '');
    setEmailBody(rule?.emailTemplate ?? t?.defaultEmail ?? '');
    setSms(rule?.smsTemplate ?? t?.defaultSms ?? '');
    setWordingOpen(false);
    setError(null);
    setInitialSnapshot(rule ? {
      alertType: key, name: rule.name || t?.name || '', sendEmail: rule.sendEmail, sendSms: rule.sendSms,
      recipientRoles: rule.recipientRoles, recipientAdminIds: rule.recipientAdminIds || [],
      extraEmails: rule.extraEmails || [], extraPhones: rule.extraPhones || [],
      subject: rule.subjectTemplate ?? t?.defaultSubject ?? '', emailBody: rule.emailTemplate ?? t?.defaultEmail ?? '',
      sms: rule.smsTemplate ?? t?.defaultSms ?? '',
    } : null);
  }, [open, rule, initialAlertType, types]);

  // Editing: Save once something changed. Adding: once it has an event, a channel and recipients
  const snapshot = useMemo(() => ({
    alertType, name, sendEmail, sendSms, recipientRoles, recipientAdminIds, extraEmails, extraPhones, subject, emailBody, sms,
  }), [alertType, name, sendEmail, sendSms, recipientRoles, recipientAdminIds, extraEmails, extraPhones, subject, emailBody, sms]);
  const hasChanges = useHasChanges(snapshot, initialSnapshot);
  const canSave = rule
    ? hasChanges
    : !!alertType && (sendEmail || sendSms) &&
      (recipientRoles.length + recipientAdminIds.length + extraEmails.length + extraPhones.length) > 0;

  const handleTypeChange = (key: string) => {
    const prev = type;
    const next = types.find(t => t.key === key);
    if (!next) return;
    if (!name.trim() || name === prev?.name) setName(next.name);
    if (!prev || subject === prev.defaultSubject) setSubject(next.defaultSubject);
    if (!prev || emailBody === prev.defaultEmail) setEmailBody(next.defaultEmail);
    if (!prev || sms === prev.defaultSms) setSms(next.defaultSms);
    setAlertType(key);
  };

  const pickedAdmins = useMemo(
    () => admins.filter(a => recipientAdminIds.includes(a.id)),
    [admins, recipientAdminIds]
  );
  const adminsWithoutPhone = sendSms ? pickedAdmins.filter(a => !a.hasPhone) : [];

  const usesCustomWording = !!type && (
    subject !== type.defaultSubject || emailBody !== type.defaultEmail || sms !== type.defaultSms
  );

  const insertVariable = (variable: string) => {
    const token = `{${variable}}`;
    const setters: Record<WordingField, [string, (v: string) => void]> = {
      subject: [subject, setSubject],
      email: [emailBody, setEmailBody],
      sms: [sms, setSms],
    };
    const [value, setValue] = setters[focusedField];
    const el = fieldRefs[focusedField].current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = async () => {
    if (!type) {
      setError('Pick an event');
      return;
    }
    if (!sendEmail && !sendSms) {
      setError('Pick at least one channel (Email or SMS)');
      return;
    }
    if (!recipientRoles.length && !recipientAdminIds.length && !extraEmails.length && !extraPhones.length) {
      setError('Add at least one recipient');
      return;
    }

    const payload: AlertRuleInput = {
      alertType,
      name: name.trim() || type.name,
      enabled: rule ? rule.enabled : true,
      sendEmail,
      sendSms,
      recipientRoles,
      recipientAdminIds,
      extraEmails,
      extraPhones,
      subjectTemplate: subject.trim() && subject !== type.defaultSubject ? subject : null,
      emailTemplate: emailBody.trim() && emailBody !== type.defaultEmail ? emailBody : null,
      smsTemplate: sms.trim() && sms !== type.defaultSms ? sms : null,
    };

    setSaving(true);
    setError(null);
    try {
      const saved = rule
        ? await adminAlertsService.updateRule(rule.id, payload)
        : await adminAlertsService.createRule(payload);
      toast.success(rule ? 'Alert updated' : 'Alert added');
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the alert');
    } finally {
      setSaving(false);
    }
  };

  const resetLink = (onClick: () => void, disabled: boolean) => (
    <Link
      component="button"
      type="button"
      variant="caption"
      onClick={onClick}
      disabled={disabled}
      sx={{ opacity: disabled ? 0.4 : 1 }}
    >
      Reset to default
    </Link>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? 'Edit alert' : 'Add alert'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <FormControl fullWidth>
            <InputLabel id="alert-event-label">Event</InputLabel>
            <Select
              labelId="alert-event-label"
              label="Event"
              value={alertType}
              onChange={e => handleTypeChange(e.target.value)}
            >
              {sortedTypes.map(t => (
                <MenuItem key={t.key} value={t.key}>
                  <Box>
                    <Typography variant="body2">{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {CATEGORY_LABELS[t.category] || t.category}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {type && <FormHelperText>{type.description}</FormHelperText>}
          </FormControl>

          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            helperText="Shown in the list and the sent alerts log"
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>Channels</Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />}
                label="Email"
              />
              <FormControlLabel
                control={<Checkbox checked={sendSms} onChange={e => setSendSms(e.target.checked)} />}
                label="SMS"
              />
            </FormGroup>
            {adminsWithoutPhone.map(a => (
              <Alert key={a.id} severity="warning" sx={{ mt: 1 }}>
                {a.name || a.email} has no phone number. Add it in their profile to send them SMS alerts.
              </Alert>
            ))}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="alert-roles-label">Everyone with role</InputLabel>
                <Select
                  labelId="alert-roles-label"
                  multiple
                  value={recipientRoles}
                  onChange={e => {
                    const v = e.target.value;
                    setRecipientRoles(typeof v === 'string' ? v.split(',') : v);
                  }}
                  input={<OutlinedInput label="Everyone with role" />}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(r => <Chip key={r} label={roleLabel(r)} size="small" />)}
                    </Box>
                  )}
                >
                  {roles.map(r => (
                    <MenuItem key={r} value={r}>
                      <Checkbox size="small" checked={recipientRoles.includes(r)} />
                      {roleLabel(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                multiple
                options={admins}
                value={pickedAdmins}
                onChange={(_e, value) => setRecipientAdminIds(value.map(a => a.id))}
                getOptionLabel={a => a.name || a.email}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(options, state) => {
                  const q = state.inputValue.toLowerCase();
                  return options.filter(a =>
                    (a.name || '').toLowerCase().includes(q) || a.email.toLowerCase().includes(q)
                  );
                }}
                renderOption={(props, a) => (
                  <li {...props} key={a.id}>
                    <Box>
                      <Typography variant="body2">
                        {a.name || a.email}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {ROLE_SINGULAR[a.role] || a.role}
                          {!a.hasPhone && ' - no phone'}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{a.email}</Typography>
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((a, index) => (
                    <Chip {...getTagProps({ index })} key={a.id} label={a.name || a.email} size="small" />
                  ))
                }
                renderInput={params => (
                  <TextField {...params} label="Specific admins" placeholder={pickedAdmins.length ? '' : 'Search by name or email'} />
                )}
              />

              <ChipListInput
                label="Extra email addresses"
                placeholder="name@example.com"
                values={extraEmails}
                onChange={setExtraEmails}
                helperText="Press Enter or comma after each address"
              />

              <ChipListInput
                label="Extra phone numbers"
                placeholder="+2348012345678 or 08012345678"
                values={extraPhones}
                onChange={setExtraPhones}
                helperText="Press Enter or comma after each number"
              />
            </Stack>
          </Box>

          <Box>
            <Button
              onClick={() => setWordingOpen(o => !o)}
              endIcon={wordingOpen ? <ExpandLess /> : <ExpandMore />}
              disabled={!type}
              sx={{ px: 0 }}
            >
              Customise wording
            </Button>
            {!wordingOpen && type && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {usesCustomWording ? 'Using your own wording' : 'Using the default wording'}
              </Typography>
            )}
            <Collapse in={wordingOpen && !!type} unmountOnExit>
              {type && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {type.variables.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Click to insert into the {focusedField === 'subject' ? 'subject' : focusedField === 'sms' ? 'SMS' : 'email body'}.
                        Wrap optional parts in {'{{#name}}...{{/name}}'}.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {type.variables.map(v => (
                          <Chip
                            key={v}
                            label={`{${v}}`}
                            size="small"
                            variant="outlined"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => insertVariable(v)}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <TextField
                      label="Email subject"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      onFocus={() => setFocusedField('subject')}
                      inputRef={fieldRefs.subject}
                      fullWidth
                    />
                    {resetLink(() => setSubject(type.defaultSubject), subject === type.defaultSubject)}
                  </Box>

                  <Box>
                    <TextField
                      label="Email body"
                      value={emailBody}
                      onChange={e => setEmailBody(e.target.value)}
                      onFocus={() => setFocusedField('email')}
                      inputRef={fieldRefs.email}
                      helperText="HTML allowed"
                      multiline
                      minRows={5}
                      maxRows={14}
                      fullWidth
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                    {resetLink(() => setEmailBody(type.defaultEmail), emailBody === type.defaultEmail)}
                  </Box>

                  <Box>
                    <TextField
                      label="SMS"
                      value={sms}
                      onChange={e => setSms(e.target.value)}
                      onFocus={() => setFocusedField('sms')}
                      inputRef={fieldRefs.sms}
                      helperText={`${sms.length} characters. 160 characters = 1 SMS (placeholders change the final length)`}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    {resetLink(() => setSms(type.defaultSms), sms === type.defaultSms)}
                  </Box>
                </Stack>
              )}
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {rule ? 'Save changes' : 'Add alert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertRuleDialog;
